import BasePage from "../../../components/layout/BasePage"
import HeadBanner from "../../../components/elements/BaseHeadBanner"
import { useContext, useEffect, useMemo, useState } from "react"
import { ModalContext } from "../../../components/contexts/ModalContext"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import Loading from "../../../components/common/Loading"
import ErrorPage from "../../404/ErrorPage"
import { getDataFromSessionStorage, useDelayWhenSearchWithPagination, useResetPageOnFilterChange } from "../../../services/functions"
import { PR } from "../../../data/Types"
import PaymentTableRow from "../components/payment/PaymentTableRow"
import RecheckPaymentModal from "../components/payment/paymentRecheckModal/RecheckPaymentModal"
import SolveEscaltionModal from "../components/payment/solveEscaltion/SolveEscaltionModal"
import { useMarkPayreqAsPaidManual, useMarkPayreqAsUnpaidManual, usePauseReminderMutation, useSolveEscalationMutation } from "../api/services"
import { Pagination } from "antd"
import { getPaginatedData } from "../../api/apiCalls"
import BaseFilterModal from "../../../components/elements/BaseFilterModal"
import BaseGrowBody from "../../../components/elements/BaseGrowBody"
import BaseTable, { ColumnType } from "../../../components/elements/BaseTable"
import PauseReminderModal from "../components/payment/PauseReminderModal"
import SearchbarAndNavigationButtons from "../components/SearchbarAndNavigationButtons"
import { GeneralQueryKeys, LocalStorageKeys } from "../../../api/constants"
import FloatingActionButton from "../components/FloatingActionButton"

export default function PaymentPage() {
    // variables for pagination
    const [page, setpage] = useState(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "page") ?? 1)
    const handlePageChange = (page: number) => {
        sessionStorage.setItem(LocalStorageKeys.PAYREQ_FILTER + "page", JSON.stringify(page))
        setpage(page)
    }

    // variables for search in pagination
    const [searchTerm, setsearchTerm] = useState(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "searchTerm"))
    const [status, setstatus] = useState(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "status"))
    const [invoiceStartDate, setinvoiceStartDate] = useState(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "dateSince"))
    const [invoiceEndDate, setinvoiceEndDate] = useState(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "dateTo"))
    const [invertSort, setinvertSort] = useState<boolean | undefined>(getDataFromSessionStorage(LocalStorageKeys.PAYREQ_FILTER + "invertSort"))
    const searchParams = useDelayWhenSearchWithPagination(searchTerm, LocalStorageKeys.PAYREQ_FILTER + "searchTerm")
    useResetPageOnFilterChange([searchParams, status, invoiceStartDate, invoiceEndDate, invertSort], () => handlePageChange(1))
    // Get Data Query
    const queryKeyPayreqs = GeneralQueryKeys.PAYREQS_ALL + page + searchParams + status + invoiceStartDate + invoiceEndDate + invertSort
    const { error, data, isLoading } = useQuery({
        queryKey: [queryKeyPayreqs],
        queryFn: () =>
            getPaginatedData({
                pageType: "paymentrequirements",
                page: page,
                limit: 15,
                search: searchParams,
                status: status,
                startDate: invoiceStartDate,
                endDate: invoiceEndDate,
                invertSort: invertSort,
            }),
    })
    const displayedPayReqs: PR[] = useMemo(() => data?.docs, [data?.docs])

    const modalCon = useContext(ModalContext)
    const [payment, setpayment] = useState<PR>()

    useEffect(() => {
        if (displayedPayReqs && payment) {
            setpayment(displayedPayReqs?.find((item: PR) => item._id === payment._id))
        }
    }, [displayedPayReqs])

    // NOTE -> used in work with Filter Modal
    const [showFilter, setshowFilter] = useState(false)

    const queryClient = useQueryClient()
    const [solvingId, setSolvingId] = useState("")
    const { mutate: solveEscalationFunction } = useSolveEscalationMutation(queryClient, modalCon, 1, queryKeyPayreqs)
    const solveEscalation = () => {
        solveEscalationFunction(solvingId)
    }

    const handleRemoveFilter = () => {
        setsearchTerm("")
        setstatus("")
        setinvoiceEndDate("")
        setinvoiceStartDate("")
    }

    const { mutate: markAsPaid } = useMarkPayreqAsPaidManual(queryClient, queryKeyPayreqs)
    const { mutate: markAsUnpaid } = useMarkPayreqAsUnpaidManual(queryClient, queryKeyPayreqs)

    // PAUSE SENDING PAYMENT REMINDER
    const { mutate: setPauseReminder } = usePauseReminderMutation(queryClient, queryKeyPayreqs)

    // FE LOGIC
    const paymentTableColumns: ColumnType[] = [
        {
            label: "Fälligkeitsdatum",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Erstellungsdatum",
            responsive: "hidden xl:table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: "Nächste Mahnung",
            responsive: "hidden 2xl:table-cell",
            width: "min-w-[10rem] w-fit",
        },
        {
            label: window.innerWidth > 640 ? "Rechnungsnummer" : "Nr.",
        },
        {
            label: "Netto",
            width: " sm:min-w-[8rem] w-fit",
        },
        {
            label: "Brutto",
            responsive: "hidden 2xl:table-cell",
            width: " sm:min-w-[8rem] w-fit",
        },
        {
            label: "Kunde",
            responsive: "hidden md:table-cell ",
        },
        {
            label: "Status",
            responsive: "hidden md:table-cell ",
        },
        {
            label: "",
        },
        {
            label: "",
        },
        {
            label: "",
        },
    ]

    return (
        <BasePage>
            <HeadBanner title={"Zahlungsaufforderungen"} />
            {/* UPPER ROW OF INPUT & BUTTON */}
            <SearchbarAndNavigationButtons
                searchBarPlaceholder="Rechnungsnummer / Kunde / Objekt suchen..."
                searchTerm={searchTerm}
                sortDirection={invertSort ?? false}
                setsearchTerm={setsearchTerm}
                sort={() => setinvertSort(!invertSort)}
                showFilter={() => setshowFilter(!showFilter)}
            />
            {isLoading && <Loading />}
            {error && <ErrorPage />}
            {data && !error && !isLoading && (
                <>
                    {/* TABLE */}
                    <BaseGrowBody classProps="overflow-x-hidden my-4">
                        <BaseTable
                            columns={paymentTableColumns}
                            tbody={displayedPayReqs?.map((item: PR) => (
                                <PaymentTableRow
                                    pausedReminder={item?.pauseNextReminderForDays}
                                    key={item?._id}
                                    id={item?.bill_id?._id}
                                    title={item?.bill_id?.nr}
                                    customer={item?.customer_id?.customer?.name}
                                    status={item?.status}
                                    invoice={item?.invoice_date}
                                    delivery={item?.due_date}
                                    nextReminder={item?.nextReminderDate as Date}
                                    netto={item?.totalNetto}
                                    brutto={item?.expected_payment}
                                    type={item?.bill_id?.billType}
                                    archived={false}
                                    escalationRequired={item?.escalationRequired}
                                    escalationSolvedDate={item?.escalationSolvedDate}
                                    openSolveEscalationModal={() => {
                                        setSolvingId(item._id)
                                        modalCon?.trigger(1)
                                    }}
                                    modalOpen={() => {
                                        setpayment(item)
                                        modalCon?.trigger(2)
                                    }}
                                    markAsPaid={() => markAsPaid(item._id)}
                                    openPauseReminderModal={() => {
                                        setpayment(item)
                                        modalCon?.trigger(3)
                                    }}
                                />
                            ))}
                        />

                        {(!displayedPayReqs || displayedPayReqs.length === 0) && <p className="text-center text-gray-300 uppercase font-medium pt-4">Keine erwarteten Zahlungen</p>}
                    </BaseGrowBody>
                    <BaseFilterModal
                        visible={showFilter}
                        searchdateSince={invoiceStartDate}
                        searchdateTo={invoiceEndDate}
                        changeDateSince={setinvoiceStartDate}
                        changeDateTo={setinvoiceEndDate}
                        searchStatus={status}
                        changeStatus={setstatus}
                        statusType={"payreq"}
                        removeFilter={handleRemoveFilter}
                        setVisible={() => setshowFilter(!showFilter)}
                        sessionStorageKey={LocalStorageKeys.PAYREQ_FILTER}
                    />
                    {/* PAGINATION */}
                    <div className="flex justify-center w-full p-4">
                        <Pagination defaultCurrent={1} current={page} pageSize={15} onChange={handlePageChange} total={data.totalDocs} showSizeChanger={false} />
                    </div>
                </>
            )}
            <FloatingActionButton currentPage="pay-req" />
            <SolveEscaltionModal modalId={1} solveEscalation={solveEscalation} />
            <RecheckPaymentModal modalId={2} payment={payment} refreshDataPayment={queryKeyPayreqs} makePayreqUnpaid={() => markAsUnpaid(payment?._id ?? "")} />
            <PauseReminderModal modalId={3} id={payment?._id ?? ""} close={() => modalCon?.trigger(3)} setPauseReminder={setPauseReminder} />
        </BasePage>
    )
}
